import { lazy, Suspense } from 'react';
import { Navigate, useRoutes } from 'react-router-dom';
import AuthGuard from 'src/components/AuthGuard';
import GuestGuard from 'src/components/GuestGuard';
import LoadingScreen from 'src/components/LoadingScreen';
import DashboardLayout from 'src/layouts/DashboardLayout';
import DocsLayout from 'src/layouts/DocsLayout';
import ApiLayout from 'src/layouts/DocsLayout/ApiLayout';
import MainLayout from 'src/layouts/MainLayout';

const Loadable = Component => props => {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  let element = useRoutes([
    {
      path: '/',
      children: [
        { element: <Navigate to="/login" replace />, index: true },
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
        {
          path: 'register',
          element: (
            <GuestGuard>
              <Register />
            </GuestGuard>
          ),
        },
        {
          path: 'ticket/:language/:ticketId',
          element: (
            <MainLayout>
              <TicketPlayer />
            </MainLayout>
          ),
        },
        {
          path: 'app',
          element: (
            <AuthGuard>
              <DashboardLayout />
            </AuthGuard>
          ),
          children: [
            {
              element: <Navigate to="/app/overview/dashboard" replace />,
              index: true,
            },
            {
              path: 'overview',
              children: [
                {
                  element: <Navigate to="/app/overview/dashboard" replace />,
                  index: true,
                },
                {
                  path: 'dashboard',
                  element: <Dashboard />,
                },
                {
                  path: 'activities',
                  element: <Activity />,
                },
                {
                  path: 'transactions',
                  element: <Transaction />,
                },
              ],
            },
            {
              path: 'management',
              children: [
                {
                  element: <Navigate to="/app/management/users" replace />,
                  index: true,
                },
                {
                  path: 'users',
                  children: [
                    { index: true, element: <UserListView /> },
                    { path: ':userId', element: <UserDetailsView /> },
                  ],
                },
                {
                  path: 'media',
                  children: [
                    { index: true, element: <MediaListView /> },
                    { path: 'add', element: <MediaFormView /> },
                    { path: ':mediaId', element: <MediaFormView /> },
                  ],
                },
                {
                  path: 'languages',
                  children: [
                    { index: true, element: <LanguageListView /> },
                    { path: 'add', element: <LanguageFormView /> },
                    { path: ':languageId', element: <LanguageDetailsView /> },
                    { path: ':languageId/translations/add', element: <TranslationFormView /> },
                    { path: ':languageId/translations/:translationId', element: <TranslationFormView /> },
                  ],
                },
                {
                  path: 'promo-codes',
                  children: [
                    { index: true, element: <PromoCodeListView /> },
                    { path: 'add', element: <PromoCodeFormView /> },
                    { path: ':promoCodeId', element: <PromoCodeFormView /> },
                  ],
                },
                {
                  path: 'products',
                  children: [
                    { index: true, element: <ProductListView /> },
                    { path: 'add', element: <ProductFormView /> },
                    { path: ':productId', element: <ProductFormView /> },
                  ],
                },
                {
                  path: 'product-packages',
                  children: [
                    { index: true, element: <ProductPackageListView /> },
                    { path: 'add', element: <ProductPackageFormView /> },
                    { path: ':productPackageId', element: <ProductPackageFormView /> },
                  ],
                },
                {
                  path: 'game-types',
                  children: [
                    { index: true, element: <GameListView /> },
                    { path: 'add', element: <GameFormView /> },
                    { path: ':gameId', element: <GameFormView /> },
                  ],
                },
                {
                  path: 'remote-configs',
                  children: [
                    { index: true, element: <RemoteConfigListView /> },
                    { path: 'add', element: <RemoteConfigFormView /> },
                    { path: ':remoteConfigId', element: <RemoteConfigFormView /> },
                  ],
                },
                {
                  path: 'whitelist-users',
                  children: [
                    { index: true, element: <WhitelistView /> },
                    { path: 'add', element: <WhitelistFormView /> },
                    { path: ':userId', element: <WhitelistFormView /> },
                  ],
                },
                {
                  path: 'videos',
                  children: [
                    { index: true, element: <VideoListView /> },
                    { path: 'add', element: <VideoFormView /> },
                    { path: ':videoId', element: <VideoEditView /> },
                  ],
                },
                {
                  path: 'categories',
                  children: [
                    { index: true, element: <CategoryListView /> },
                    { path: 'add', element: <CategoryFormView /> },
                    { path: ':categoryId', element: <CategoryFormView /> },
                  ],
                },
                {
                  path: 'motor-skills',
                  children: [
                    { index: true, element: <MotorSkillListView /> },
                    { path: 'add', element: <MotorSkillFormView /> },
                    { path: ':skillId', element: <MotorSkillFormView /> },
                  ],
                },
                {
                  path: 'lullabies',
                  children: [
                    { index: true, element: <LullabyAndSongListView /> },
                    { path: 'add', element: <LullabyAndSongFormView /> },
                    { path: ':lullabyId', element: <LullabyAndSongEditView /> },
                  ],
                },
              ],
            },
            {
              path: 'application',
              children: [
                {
                  element: <Navigate to="/app/application/logs" replace />,
                  index: true,
                },
                {
                  path: 'logs',
                  element: <LogListView />,
                },
                {
                  path: 'tickets',
                  children: [
                    { index: true, element: <Ticket /> },
                    { path: ':ticketId', element: <Ticket /> },
                  ],
                },
              ],
            },
            {
              path: 'form',
              children: [
                {
                  element: <Navigate to="/app/form/feedback" replace />,
                  index: true,
                },
                {
                  path: 'feedback',
                  element: <Feedback />,
                },
                {
                  path: 'contact',
                  element: <ContactPage />,
                },
                {
                  path: 'career',
                  element: <CareerPage />,
                },
              ],
            },
            {
              path: 'account',
              element: <AccountView />,
            },
          ],
        },

        {
          path: 'docs',
          element: (
            <AuthGuard>
              <DocsLayout />
            </AuthGuard>
          ),
          children: [
            { element: <Navigate to="/docs/welcome" replace />, index: true },
            { path: 'welcome', element: <WelcomeView /> },
            { path: 'getting-started', element: <GettingStartedView /> },
            { path: 'postman', element: <PostmanView /> },
            {
              path: 'api',
              element: <ApiLayout />,
              children: [
                { element: <ApiView />, index: true },
                { path: 'authentication', element: <AuthenticationView /> },
                { path: 'me', element: <MeView /> },
                { path: 'feedback', element: <FeedbackView /> },
                { path: 'mail', element: <MailView /> },
                { path: 'cache', element: <CacheView /> },
                { path: 'metadata', element: <MetadataView /> },
                { path: 'localization', element: <LocalizationView /> },
                { path: 'promo-code', element: <PromoCodeView /> },
                { path: 'ticket', element: <TicketView /> },
                { path: 'store', element: <StoreView /> },
                { path: 'report', element: <ReportView /> },
                { path: 'realtime-notification', element: <RealtimeNotificationView /> },
                { path: 'video', element: <VideoView /> },
                { path: 'lullaby', element: <LullabyView /> },
                { path: 'whitelist', element: <WhitelistDocView /> },
              ],
            },
            { path: 'support', element: <SupportView /> },
            { path: 'changelog', element: <ChangelogView /> },
          ],
        },
        { path: '*', element: <Navigate to="/404" replace /> },
        { path: '404', element: <NotFoundView /> },
      ],
    },
  ]);
  return element;
}

// AUTH
const Login = Loadable(lazy(() => import('src/views/auth/LoginView')));
const Register = Loadable(lazy(() => import('src/views/auth/RegisterView')));
const NotFoundView = Loadable(lazy(() => import('src/views/errors/NotFoundView')));

// DASHBOARD
const TicketPlayer = Loadable(lazy(() => import('src/views/ticket/Player.js')));
const Dashboard = Loadable(lazy(() => import('src/views/overview/Dashboard')));
const Activity = Loadable(lazy(() => import('src/views/overview/Activity')));
const Transaction = Loadable(lazy(() => import('src/views/overview/Transaction')));
const UserListView = Loadable(lazy(() => import('src/views/users/UserListView')));
const UserDetailsView = Loadable(lazy(() => import('src/views/users/UserDetailsView')));
const MediaListView = Loadable(lazy(() => import('src/views/media/MediaListView')));
const MediaFormView = Loadable(lazy(() => import('src/views/media/MediaFormView')));
const LanguageListView = Loadable(lazy(() => import('src/views/language/LanguageListView')));
const LanguageDetailsView = Loadable(lazy(() => import('src/views/language/LanguageDetailsView')));
const LanguageFormView = Loadable(lazy(() => import('src/views/language/LanguageFormView')));
const TranslationFormView = Loadable(lazy(() => import('src/views/language/TranslationFormView')));
const PromoCodeListView = Loadable(lazy(() => import('src/views/promoCode/PromoCodeListView')));
const PromoCodeFormView = Loadable(lazy(() => import('src/views/promoCode/PromoCodeFormView')));
const ProductListView = Loadable(lazy(() => import('src/views/product/ProductListView')));
const ProductFormView = Loadable(lazy(() => import('src/views/product/ProductFormView')));
const ProductPackageListView = Loadable(lazy(() => import('src/views/productPackage/ProductPackageListView')));
const ProductPackageFormView = Loadable(lazy(() => import('src/views/productPackage/ProductPackageFormView')));
const Ticket = Loadable(lazy(() => import('src/views/ticket')));
const LogListView = Loadable(lazy(() => import('src/views/log/LogListView')));
const Feedback = Loadable(lazy(() => import('src/views/forms/Feedback')));
const ContactPage = Loadable(lazy(() => import('src/views/forms/ContactPage')));
const CareerPage = Loadable(lazy(() => import('src/views/forms/CareerPage')));
const AccountView = Loadable(lazy(() => import('src/views/account/AccountView')));
const CategoryListView = Loadable(lazy(() => import('src/views/category/CategoryListView')));
const CategoryFormView = Loadable(lazy(() => import('src/views/category/CategoryFormView')));
const MotorSkillListView = Loadable(lazy(() => import('src/views/motorSkill/MotorSkillListView')));
const MotorSkillFormView = Loadable(lazy(() => import('src/views/motorSkill/MotorSkillFormView')));
const VideoListView = Loadable(lazy(() => import('src/views/video/VideoListView')));
const VideoFormView = Loadable(lazy(() => import('src/views/video/VideoFormView')));
const VideoEditView = Loadable(lazy(() => import('src/views/video/VideoEditView')));
const LullabyAndSongListView = Loadable(lazy(() => import('src/views/lullabyAndSong/LullabyAndSongListView')));
const LullabyAndSongFormView = Loadable(lazy(() => import('src/views/lullabyAndSong/LullabyAndSongFormView')));
const LullabyAndSongEditView = Loadable(lazy(() => import('src/views/lullabyAndSong/LullabyAndSongEditView')));
const GameListView = Loadable(lazy(() => import('src/views/game/GameListView')));
const GameFormView = Loadable(lazy(() => import('src/views/game/GameFormView')));
const RemoteConfigListView = Loadable(lazy(() => import('src/views/remoteConfig/RemoteConfigListView')));
const RemoteConfigFormView = Loadable(lazy(() => import('src/views/remoteConfig/RemoteConfigFormView')));
const WhitelistView = Loadable(lazy(() => import('src/views/whitelist/WhitelistView')));
const WhitelistFormView = Loadable(lazy(() => import('src/views/whitelist/WhitelistFormView')));

// DOCS
const WelcomeView = Loadable(lazy(() => import('src/views/docs/WelcomeView')));
const GettingStartedView = Loadable(lazy(() => import('src/views/docs/GettingStartedView')));
const PostmanView = Loadable(lazy(() => import('src/views/docs/PostmanView')));
const ApiView = Loadable(lazy(() => import('src/views/docs/ApiView')));
const AuthenticationView = Loadable(lazy(() => import('src/views/docs/ApiView/AuthenticationView')));
const MeView = Loadable(lazy(() => import('src/views/docs/ApiView/MeView')));
const FeedbackView = Loadable(lazy(() => import('src/views/docs/ApiView/FeedbackView')));
const MailView = Loadable(lazy(() => import('src/views/docs/ApiView/MailView')));
const CacheView = Loadable(lazy(() => import('src/views/docs/ApiView/CacheView')));
const MetadataView = Loadable(lazy(() => import('src/views/docs/ApiView/MetadataView')));
const LocalizationView = Loadable(lazy(() => import('src/views/docs/ApiView/LocalizationView')));
const StoreView = Loadable(lazy(() => import('src/views/docs/ApiView/StoreView')));
const ReportView = Loadable(lazy(() => import('src/views/docs/ApiView/ReportView')));
const RealtimeNotificationView = Loadable(lazy(() => import('src/views/docs/ApiView/RealtimeNotificationView')));
const VideoView = Loadable(lazy(() => import('src/views/docs/ApiView/VideoView')));
const LullabyView = Loadable(lazy(() => import('src/views/docs/ApiView/LullabyView')));
const WhitelistDocView = Loadable(lazy(() => import('src/views/docs/ApiView/WhitelistView')));
const PromoCodeView = Loadable(lazy(() => import('src/views/docs/ApiView/PromoCodeView')));
const TicketView = Loadable(lazy(() => import('src/views/docs/ApiView/TicketView')));
const SupportView = Loadable(lazy(() => import('src/views/docs/SupportView')));
const ChangelogView = Loadable(lazy(() => import('src/views/docs/ChangelogView')));
